import React from "react";
import { Button, Table } from "antd";
import type { ColumnsType } from "antd/es/table";

import { formatDailyRecapDate } from "../utils/dailyRecapDisplay";

interface StockOutageRow {
  productId: number;
  productName: string;
  currentStock: number;
  alertQuantity: number;
  unitShortName: string;
}

interface DailyRecapStockOutagesProps {
  snapshot: Record<string, unknown>;
  warehouseName: unknown;
  recapDate: unknown;
  onClose: () => void;
}

const formatQuantity = (value: number, unit: string) =>
  `${value.toLocaleString("fr-FR", { maximumFractionDigits: 5 })} ${unit}`;

const formatCapturedAt = (value: unknown) => {
  if (typeof value !== "string") return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Africa/Abidjan",
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
};

const normalizeRows = (value: unknown): StockOutageRow[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return [];
    const row = item as Record<string, unknown>;
    const productId = Number(row.product_id);
    const currentStock = Number(row.current_stock);
    const alertQuantity = Number(row.alert_quantity);
    if (
      !Number.isSafeInteger(productId) || productId <= 0 ||
      !Number.isFinite(currentStock) || !Number.isFinite(alertQuantity)
    ) {
      return [];
    }

    return [{
      productId,
      productName: String(row.product_name || "Produit"),
      currentStock,
      alertQuantity,
      unitShortName: String(row.unit_short_name || "unité(s)"),
    }];
  });
};

const columns: ColumnsType<StockOutageRow> = [
  {
    title: "Produit",
    dataIndex: "productName",
    key: "productName",
  },
  {
    title: "Stock relevé",
    key: "currentStock",
    render: (_, row) => formatQuantity(row.currentStock, row.unitShortName),
  },
  {
    title: "Seuil d’alerte",
    key: "alertQuantity",
    render: (_, row) => formatQuantity(row.alertQuantity, row.unitShortName),
  },
];

const DailyRecapStockOutages: React.FC<DailyRecapStockOutagesProps> = ({
  snapshot,
  warehouseName,
  recapDate,
  onClose,
}) => {
  const rows = normalizeRows(snapshot.items);
  const capturedAt = formatCapturedAt(snapshot.captured_at);
  const warehouse =
    typeof warehouseName === "string" && warehouseName.trim()
      ? warehouseName.trim()
      : "Établissement sélectionné";
  const date =
    typeof recapDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(recapDate)
      ? formatDailyRecapDate(recapDate)
      : null;

  return (
    <section className="daily-recap-stock-outages" aria-label="Détail des ruptures au moment du bilan">
      <div className="daily-recap-stock-outages__heading">
        <div>
          <h3>Ruptures relevées · {warehouse}</h3>
          <p>
            {rows.length} produit{rows.length > 1 ? "s" : ""} en rupture
            {date ? ` pour le bilan du ${date}` : ""}
            {capturedAt ? ` · relevé le ${capturedAt}` : ""}.
          </p>
        </div>
        <Button onClick={onClose}>Masquer le détail</Button>
      </div>
      <p className="daily-recap-stock-outages__note">
        Le relevé couvre les produits avec un seuil d’alerte configuré et un stock nul ou négatif.
        Ces valeurs ont été enregistrées lors de la génération du bilan ; le stock actuel peut avoir changé.
      </p>
      <Table<StockOutageRow>
        className="daily-recap-stock-outages__table"
        columns={columns}
        dataSource={rows}
        rowKey="productId"
        size="small"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        scroll={{ x: 520 }}
        locale={{ emptyText: "Aucune rupture enregistrée dans ce relevé." }}
      />
    </section>
  );
};

export default DailyRecapStockOutages;
